import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Spinner } from 'react-bootstrap';
import { fetchListRegisteredVehicles, updateVehicleStatus, updateRegisteredVehicle } from '../../services/adminService';
import { toast } from 'react-toastify';
import './AdminVehicles.css';

// Server API base URL for serving static files (vehicle images)
const SERVER_API_URL = import.meta.env.VITE_API_URL || '';
const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${SERVER_API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const AdminVehicles = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [stationFilter, setStationFilter] = useState('ALL');

    // Detail Modal State
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await fetchListRegisteredVehicles();
            if (res && res.ok && Array.isArray(res.data)) {
                setVehicles(res.data);
            } else if (Array.isArray(res?.data)) {
                setVehicles(res.data);
            } else {
                setVehicles([]);
            }
        } catch (error) {
            console.error('Error fetching registered vehicles:', error);
            toast.error('Không thể nạp danh sách phương tiện đăng ký');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const isMotorbike = (type) => {
        const t = (type || '').toLowerCase();
        return t.includes('motorcycle') || t.includes('xe máy') || t.includes('xe may');
    };

    const isCar = (type) => {
        const t = (type || '').toLowerCase();
        return t.includes('car') || t.includes('ô tô') || t.includes('o to');
    };

    // Filter logic
    const filteredVehicles = useMemo(() => {
        return vehicles.filter(item => {
            const matchSearch = (item.licensePlate || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.ownerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.userCode || '').toLowerCase().includes(searchQuery.toLowerCase());

            let matchType = true;
            if (typeFilter === 'MOTOR') matchType = isMotorbike(item.vehicleType);
            if (typeFilter === 'CAR') matchType = isCar(item.vehicleType);

            let matchStation = true;
            if (stationFilter === 'INSTATION') matchStation = Boolean(item.inStation);
            if (stationFilter === 'OUTSTATION') matchStation = !item.inStation;

            return matchSearch && matchType && matchStation;
        });
    }, [vehicles, searchQuery, typeFilter, stationFilter]);

    // KPI Metrics calculation
    const metrics = useMemo(() => {
        const totalCount = filteredVehicles.length;
        const motorCount = filteredVehicles.filter(v => isMotorbike(v.vehicleType)).length;
        const carCount = filteredVehicles.filter(v => isCar(v.vehicleType)).length;
        const inStationCount = filteredVehicles.filter(v => Boolean(v.inStation)).length;

        return { totalCount, motorCount, carCount, inStationCount };
    }, [filteredVehicles]);

    // Pagination slice
    const paginatedVehicles = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredVehicles.slice(start, start + pageSize);
    }, [filteredVehicles, currentPage]);

    const totalPages = Math.ceil(filteredVehicles.length / pageSize) || 1;

    const formatDate = (dateVal) => {
        if (!dateVal) return '-';
        try {
            return new Date(dateVal).toLocaleDateString('vi-VN', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return String(dateVal);
        }
    };

    const handleOpenDetail = (vItem) => {
        setSelectedVehicle(vItem);
        setShowDetailModal(true);
    };

    const handleOpenEdit = (vItem) => {
        setSelectedVehicle(vItem);
        setEditData({ ...vItem });
        setShowEditModal(true);
    };

    const handleSaveVehicle = async () => {
        if (!selectedVehicle) return;
        setIsSaving(true);
        try {
            const res = await updateRegisteredVehicle({
                userCode: selectedVehicle.userCode,
                vehicleId: selectedVehicle.id,
                updateData: editData
            });
            if (res?.ok) {
                toast.success('Cập nhật phương tiện thành công');
                setShowEditModal(false);
                await loadData();
            } else {
                toast.error(res?.message || 'Không thể cập nhật phương tiện');
            }
        } catch (error) {
            console.error('Error updating vehicle:', error);
            toast.error('Lỗi khi cập nhật phương tiện');
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuickToggleStatus = async (item) => {
        setSelectedVehicle(item);
        setIsUpdatingStatus(true);
        try {
            const newStatus = item.status === 'active' ? 'inactive' : 'active';
            const res = await updateVehicleStatus({
                userCode: item.userCode,
                licensePlate: item.licensePlate,
                status: newStatus
            });
            if (res?.ok) {
                toast.success(newStatus === 'active' ? 'Đã kích hoạt phương tiện' : 'Đã vô hiệu hóa phương tiện');
                await loadData();
            } else toast.error(res?.message || 'Không thể đổi trạng thái phương tiện');
        } catch (error) {
            toast.error('Lỗi khi đổi trạng thái phương tiện');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleToggleStatus = async (newStatus) => {
        if (!selectedVehicle) return;
        setIsUpdatingStatus(true);
        try {
            const res = await updateVehicleStatus({
                userCode: selectedVehicle.userCode,
                licensePlate: selectedVehicle.licensePlate,
                status: newStatus
            });

            if (res && res.ok) {
                toast.success(`Đã cập nhật trạng thái phương tiện ${selectedVehicle.licensePlate} thành ${newStatus === 'active' ? 'Đã duyệt / Hoạt động' : 'Tạm khóa'}!`);
                setShowDetailModal(false);
                loadData();
            } else {
                toast.error(res?.message || 'Không thể cập nhật trạng thái phương tiện');
            }
        } catch (error) {
            console.error('Error updating vehicle status:', error);
            toast.error('Lỗi khi cập nhật trạng thái phương tiện');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    return (
        <div className="admin-vehicles-page">
            <Helmet>
                <title>Quản lý phương tiện người dùng đăng ký</title>
            </Helmet>

            <Container fluid className="py-4 px-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-car-front-fill text-primary fs-3"></i>
                            Quản Lý Phương Tiện Người Dùng Đăng Ký
                        </h3>
                        <p className="text-secondary mb-0">Theo dõi thông tin cà vẹt, kiểm tra biển số & duyệt phương tiện cá nhân</p>
                    </div>
                </div>

                {/* Metric KPI Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card primary-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Tổng số phương tiện đăng ký</span>
                                    <h4 className="fw-bold text-primary mb-0 mt-1">{metrics.totalCount} xe</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-primary bg-opacity-10 text-primary p-3">
                                    <i className="bi bi-shield-check fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card warning-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Số lượng Xe máy</span>
                                    <h4 className="fw-bold text-warning mb-0 mt-1">{metrics.motorCount} xe</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-warning bg-opacity-10 text-warning p-3">
                                    <i className="bi bi-bicycle fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card info-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Số lượng Ô tô</span>
                                    <h4 className="fw-bold text-info mb-0 mt-1">{metrics.carCount} xe</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-info bg-opacity-10 text-info p-3">
                                    <i className="bi bi-car-front-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 bg-white metric-card success-accent">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold">Xe đang đỗ trong bãi</span>
                                    <h4 className="fw-bold text-success mb-0 mt-1">{metrics.inStationCount} xe</h4>
                                </div>
                                <div className="metric-icon rounded-circle bg-success bg-opacity-10 text-success p-3">
                                    <i className="bi bi-p-square-fill fs-4"></i>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Filter Controls Bar */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 p-3 bg-white">
                    <Row className="g-3 align-items-center">
                        <Col lg={5}>
                            <div className="position-relative">
                                <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"></i>
                                <Form.Control 
                                    type="text" 
                                    placeholder="Tìm theo Biển số xe (VD: 51F-069.48), Tên chủ xe, Mã người dùng..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="ps-5 rounded-3 border-light-subtle"
                                />
                            </div>
                        </Col>

                        <Col lg={3}>
                            <Form.Select 
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="rounded-3 border-light-subtle fw-medium"
                            >
                                <option value="ALL">Tất cả loại phương tiện</option>
                                <option value="MOTOR">Xe máy (Motorcycle)</option>
                                <option value="CAR">Ô tô (Car)</option>
                            </Form.Select>
                        </Col>

                        <Col lg={2}>
                            <Form.Select 
                                value={stationFilter}
                                onChange={(e) => setStationFilter(e.target.value)}
                                className="rounded-3 border-light-subtle fw-medium"
                            >
                                <option value="ALL">Tất cả vị trí</option>
                                <option value="INSTATION">Đang trong bãi</option>
                                <option value="OUTSTATION">Đã ra ngoài</option>
                            </Form.Select>
                        </Col>

                        <Col lg={2} className="text-end">
                            <Button 
                                variant="light" 
                                className="border rounded-3 fw-semibold text-secondary w-100"
                                onClick={() => { setSearchQuery(''); setTypeFilter('ALL'); setStationFilter('ALL'); }}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Xóa lọc
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Vehicles Table */}
                <Card className="border-0 shadow-sm rounded-4 p-4 bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-card-checklist text-primary"></i>
                            Danh Sách Phương Tiện Cá Nhân ({filteredVehicles.length})
                        </h5>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Đang nạp danh sách phương tiện...</p>
                        </div>
                    ) : paginatedVehicles.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <Table hover className="modern-table align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>STT</th>
                                            <th>Biển Số Xe</th>
                                            <th>Mã Người Dùng</th>
                                            <th>Tên Chủ Xe</th>
                                            <th>Loại Xe</th>
                                            <th>Hãng & Mẫu Xe</th>
                                            <th>Màu Sơn</th>
                                            <th>Vị Trí Đỗ Bãi</th>
                                            <th>Trạng Thái</th>
                                            <th className="text-center">Thao Tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedVehicles.map((item, index) => (
                                            <tr key={`veh-${index}`}>
                                                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <span className="badge bg-dark text-white font-monospace px-3 py-2 fs-6 shadow-xs border border-secondary">
                                                        {item.licensePlate}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-primary-subtle text-primary font-monospace px-2 py-1 fs-6">
                                                        {item.userCode}
                                                    </span>
                                                </td>
                                                <td><span className="fw-semibold text-dark">{item.ownerName}</span></td>
                                                <td>
                                                    <span className="fw-medium text-dark">
                                                        {isMotorbike(item.vehicleType) ? 'Xe máy' : 'Ô tô'}
                                                    </span>
                                                </td>
                                                <td className="small fw-semibold text-secondary">
                                                    {item.brand} {item.modelCode}
                                                </td>
                                                <td className="small">{item.color}</td>
                                                <td>
                                                    {item.inStation ? (
                                                        <Badge bg="success-subtle" className="text-success border border-success-subtle px-2 py-1">
                                                            <i className="bi bi-geo-alt-fill me-1"></i> Đang ở Khu {item.inStation}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="light" className="text-secondary border px-2 py-1">
                                                            Đã ra khỏi bãi
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge bg={item.status === 'active' ? 'success' : 'secondary'} className="px-2 py-1">
                                                        {item.status === 'active' ? 'Đang hoạt động' : 'Đã vô hiệu hóa'}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm" 
                                                        className="rounded-3 fw-semibold px-3 py-1 d-inline-flex align-items-center gap-1 shadow-xs"
                                                        onClick={() => handleOpenDetail(item)}
                                                    >
                                                        <i className="bi bi-file-text-fill"></i>
                                                        <span>Xem Cà Vẹt</span>
                                                    </Button>
                                                    <Button variant="outline-warning" size="sm" className="rounded-3 fw-semibold" onClick={() => handleOpenEdit(item)}>
                                                        <i className="bi bi-pencil-square me-1"></i>Sửa
                                                    </Button>
                                                    <Button
                                                        variant={item.status === 'active' ? 'outline-danger' : 'outline-success'}
                                                        size="sm"
                                                        className="rounded-3 fw-semibold"
                                                        disabled={isUpdatingStatus}
                                                        onClick={() => handleQuickToggleStatus(item)}
                                                    >
                                                        <i className={`bi ${item.status === 'active' ? 'bi-slash-circle' : 'bi-check-circle'} me-1`}></i>
                                                        {item.status === 'active' ? 'Vô hiệu hóa' : 'Kích hoạt'}
                                                    </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span className="small text-muted">
                                        Hiển thị {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredVehicles.length)} trên tổng số {filteredVehicles.length} phương tiện
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(p => p - 1)}
                                            className="border px-3 rounded-2"
                                        >
                                            &lt; Trước
                                        </Button>
                                        <span className="px-3 py-1 bg-light border rounded-2 small fw-bold">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(p => p + 1)}
                                            className="border px-3 rounded-2"
                                        >
                                            Sau &gt;
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
                            Không có phương tiện nào thỏa mãn từ khóa tìm kiếm
                        </div>
                    )}
                </Card>

                {/* Detail Vehicle Modal */}
                <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="rounded-4">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold text-primary d-flex align-items-center gap-2">
                            <i className="bi bi-card-text fs-3"></i>
                            Hồ Sơ Đăng Ký Phương Tiện
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        {selectedVehicle && (
                            <>
                                <div className="bg-dark text-white p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="small text-light d-block">Biển Số Đăng Ký</span>
                                        <h3 className="fw-bold mb-0 font-monospace text-warning">{selectedVehicle.licensePlate}</h3>
                                    </div>
                                    <div className="text-end">
                                        <span className="small text-light d-block">Chủ Sở Hữu</span>
                                        <h5 className="fw-semibold mb-0">{selectedVehicle.ownerName} ({selectedVehicle.userCode})</h5>
                                    </div>
                                </div>

                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Thông Số Kỹ Thuật XE</h6>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Loại phương tiện:</span>
                                                <span className="fw-semibold">{isMotorbike(selectedVehicle.vehicleType) ? 'Xe máy' : 'Ô tô'}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Nhãn hiệu / Model:</span>
                                                <span className="fw-semibold">{selectedVehicle.brand} {selectedVehicle.modelCode}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Màu sơn xe:</span>
                                                <span className="fw-semibold">{selectedVehicle.color}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Dung tích xylanh:</span>
                                                <span className="fw-semibold">{selectedVehicle.capacity} cc</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Số khung (Chassis):</span>
                                                <span className="font-monospace fw-semibold">{selectedVehicle.chassisNumber}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className="text-secondary small">Số máy (Engine):</span>
                                                <span className="font-monospace fw-semibold">{selectedVehicle.engineNumber}</span>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <div className="p-3 bg-light rounded-3 border h-100">
                                            <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Thông Tin Giấy Chứng Nhận</h6>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Cơ quan cấp giấy:</span>
                                                <span className="fw-semibold">{selectedVehicle.issuingAuthority}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Ngày đăng ký lần đầu:</span>
                                                <span className="fw-semibold">{formatDate(selectedVehicle.firstRegistrationDate)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Ngày cấp giấy tờ:</span>
                                                <span className="fw-semibold">{formatDate(selectedVehicle.issuedDate)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary small">Địa chỉ đăng ký:</span>
                                                <span className="fw-semibold">{selectedVehicle.address}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className="text-secondary small">Trạng thái hồ sơ:</span>
                                                <Badge bg={selectedVehicle.status === 'active' ? 'success' : 'secondary'}>
                                                    {selectedVehicle.status === 'active' ? 'Đã duyệt kích hoạt' : 'Chờ duyệt'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Registration Certificate Images */}
                                <div className="mb-3">
                                    <h6 className="fw-bold text-dark mb-2">Hình Ảnh Giấy Cà Vẹt & Biển Số Xe</h6>
                                    {Array.isArray(selectedVehicle.url) && selectedVehicle.url.length > 0 ? (
                                        <div className="d-flex gap-3 overflow-auto pb-2">
                                            {selectedVehicle.url.map((imgUrl, idx) => (
                                                <img 
                                                    key={idx} 
                                                    src={getImageUrl(imgUrl)} 
                                                    alt={`Cà vẹt ${idx + 1}`} 
                                                    className="rounded-3 border shadow-xs"
                                                    style={{ width: '220px', height: '140px', objectFit: 'cover' }}
                                                    onError={(e) => { e.target.style.display='none'; }}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-light rounded-3 text-center text-muted border">
                                            Chưa tải lên ảnh cà vẹt phương tiện
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
                        <Button variant="light" className="rounded-3 px-4 fw-semibold" onClick={() => setShowDetailModal(false)}>
                            Đóng
                        </Button>
                        {selectedVehicle && (
                            selectedVehicle.status === 'active' ? (
                                <Button 
                                    variant="outline-danger" 
                                    className="rounded-3 px-4 fw-bold"
                                    disabled={isUpdatingStatus}
                                    onClick={() => handleToggleStatus('inactive')}
                                >
                                    {isUpdatingStatus ? <Spinner animation="border" size="sm" /> : 'Tạm Khóa Phương Tiện'}
                                </Button>
                            ) : (
                                <Button 
                                    variant="success" 
                                    className="rounded-3 px-4 fw-bold shadow-sm"
                                    disabled={isUpdatingStatus}
                                    onClick={() => handleToggleStatus('active')}
                                >
                                    {isUpdatingStatus ? <Spinner animation="border" size="sm" /> : 'Phê Duyệt Phương Tiện'}
                                </Button>
                            )
                        )}
                    </Modal.Footer>
                </Modal>

                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="fw-bold">Sửa thông tin phương tiện</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3">
                            {[
                                ['ownerName', 'Tên chủ xe'], ['brand', 'Nhãn hiệu'],
                                ['modelCode', 'Mẫu xe'], ['color', 'Màu sơn'],
                                ['capacity', 'Dung tích (cc)'], ['engineNumber', 'Số máy'],
                                ['chassisNumber', 'Số khung'], ['issuingAuthority', 'Cơ quan cấp']
                            ].map(([field, label]) => (
                                <Col md={6} key={field}>
                                    <Form.Label className="fw-semibold">{label}</Form.Label>
                                    <Form.Control
                                        type={field === 'capacity' ? 'number' : 'text'}
                                        value={editData[field] ?? ''}
                                        onChange={(e) => setEditData(prev => ({
                                            ...prev,
                                            [field]: field === 'capacity' ? Number(e.target.value) : e.target.value
                                        }))}
                                    />
                                </Col>
                            ))}
                            <Col md={12}>
                                <Form.Label className="fw-semibold">Địa chỉ đăng ký</Form.Label>
                                <Form.Control value={editData.address ?? ''} onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))} />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Ngày đăng ký lần đầu</Form.Label>
                                <Form.Control type="date" value={editData.firstRegistrationDate ? String(editData.firstRegistrationDate).slice(0, 10) : ''} onChange={(e) => setEditData(prev => ({ ...prev, firstRegistrationDate: e.target.value }))} />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Ngày cấp giấy tờ</Form.Label>
                                <Form.Control type="date" value={editData.issuedDate ? String(editData.issuedDate).slice(0, 10) : ''} onChange={(e) => setEditData(prev => ({ ...prev, issuedDate: e.target.value }))} />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={() => setShowEditModal(false)}>Hủy</Button>
                        <Button variant="primary" disabled={isSaving} onClick={handleSaveVehicle}>
                            {isSaving ? <Spinner animation="border" size="sm" /> : 'Lưu thay đổi'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default AdminVehicles;
